import { useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "react-router-dom";

import { campaignsApi } from "../api/campaigns";
import { contactsApi } from "../api/contacts";
import { OPEN_TASK_STATUSES } from "../api/tasks";
import { ContactTimeline } from "../components/ContactTimeline";
import { EditableField } from "../components/EditableField";
import { JarvisComposer } from "../components/JarvisComposer";
import { LiveIndicator } from "../components/LiveIndicator";
import { PageHeader } from "../components/PageHeader";
import { QueryBoundary } from "../components/QueryBoundary";
import { StatTile } from "../components/StatTile";
import { CampaignMemberships } from "../components/contact/CampaignMemberships";
import { PipelineMemberships } from "../components/contact/PipelineMemberships";
import { ContactDetailSkeleton } from "../components/contact/ContactDetailSkeleton";
import { ContactHero } from "../components/contact/ContactHero";
import { ContactOverviewFields } from "../components/contact/ContactOverviewFields";
import { OpenTasksPanel } from "../components/contact/OpenTasksPanel";
import { useLiveUpdates } from "../lib/hooks/useLiveUpdates";
import { daysSince } from "../lib/formatRelative";
import { setIn } from "../lib/setIn";
import type { Contact, ContactData } from "../types";

const INTERACTION_PAGE = 50;

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [interactionLimit, setInteractionLimit] = useState(INTERACTION_PAGE);

  const contactQuery = useQuery({
    queryKey: ["contact", id],
    queryFn: () => contactsApi.get(id!),
    enabled: !!id,
  });
  const tasksQuery = useQuery({
    queryKey: ["contact-tasks", id],
    queryFn: () => contactsApi.tasks(id!),
    enabled: !!id,
  });
  const interactionsQuery = useQuery({
    queryKey: ["contact-interactions", id, interactionLimit],
    queryFn: () => contactsApi.interactions(id!, interactionLimit),
    enabled: !!id,
  });

  // Merge-PATCH mutation (shallow path → scalar/array). Uses optimistic
  // update so inline edits feel instant; live-update will reconcile.
  const patchMutation = useMutation({
    mutationFn: ({ path, value }: { path: string[]; value: unknown }) => {
      const partial = setIn({} as ContactData, path, value);
      return contactsApi.patch(id!, partial as Record<string, unknown>);
    },
    onMutate: async ({ path, value }) => {
      await queryClient.cancelQueries({ queryKey: ["contact", id] });
      const prev = queryClient.getQueryData<Contact>(["contact", id]);
      if (prev) {
        const nextData = setIn(prev.data, path, value) as ContactData;
        queryClient.setQueryData<Contact>(["contact", id], {
          ...prev,
          data: nextData,
        });
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(["contact", id], ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["contact", id] });
    },
  });

  const unenrollMutation = useMutation({
    mutationFn: (slug: string) => campaignsApi.unenroll(slug, id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contact", id] });
    },
  });

  // ---- live updates ----------------------------------------------------

  const topics = useMemo(
    () =>
      id
        ? [
            `contacts:${id}`,
            `tasks:${id}`,
            `interactions:${id}`,
          ]
        : [],
    [id],
  );
  const { status: liveStatus } = useLiveUpdates(topics, (ev) => {
    if (ev.table === "contacts") {
      if (ev.op === "DELETE") {
        navigate("/contacts", { replace: true });
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["contact", id] });
    }
    if (ev.table === "tasks") {
      queryClient.invalidateQueries({ queryKey: ["contact-tasks", id] });
    }
    if (ev.table === "interactions") {
      queryClient.invalidateQueries({ queryKey: ["contact-interactions", id] });
    }
  });

  const onPatch = (path: string[], value: unknown) =>
    patchMutation.mutateAsync({ path, value });

  if (contactQuery.isLoading) {
    return (
      <div className="page">
        <PageHeader
          title="Contact"
          actions={
            <Link to="/contacts" className="btn btn-ghost btn-sm">
              ← Back to directory
            </Link>
          }
        />
        <ContactDetailSkeleton />
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Contact"
        actions={
          <>
            <LiveIndicator status={liveStatus} />
            <Link to="/contacts" className="btn btn-ghost btn-sm">
              ← Back to directory
            </Link>
          </>
        }
      />

      <QueryBoundary query={contactQuery}>
        {(contact) => {
          const personal = contact.data.personal;
          const metadata = contact.data.metadata ?? {};
          const memberships = contact.data.memberships?.campaigns ?? [];
          const pipelineMemberships = contact.data.memberships?.pipelines ?? [];
          const name = `${personal.first_name} ${personal.last_name}`.trim();

          const tasks = tasksQuery.data ?? [];
          // Open = not closed; the outbound desk's attention items are not tasks.
          const openTasks = tasks.filter(
            (t) =>
              OPEN_TASK_STATUSES.includes(t.data.status) && t.data.task_type !== "attention",
          );
          const activeCampaigns = memberships.filter(
            (m) => m.status === "active",
          );

          const interactions = interactionsQuery.data ?? [];
          const hasMoreInteractions =
            (interactionsQuery.data?.length ?? 0) >= interactionLimit;

          return (
            <div className="space-y-6">
              <ContactHero contact={contact} />

              {/* ---- STAT STRIP ---- */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatTile
                  label="Interactions"
                  figure={
                    interactionsQuery.isLoading
                      ? "…"
                      : String(interactions.length)
                  }
                  delta={hasMoreInteractions ? "+ more" : undefined}
                />
                <StatTile
                  label="Days since contact"
                  figure={
                    metadata.last_contact_at
                      ? String(daysSince(metadata.last_contact_at) ?? "—")
                      : "—"
                  }
                  deltaTone={
                    (daysSince(metadata.last_contact_at) ?? 0) > 30
                      ? "warn"
                      : null
                  }
                  delta={
                    (daysSince(metadata.last_contact_at) ?? 0) > 30
                      ? "> 30 days ago"
                      : undefined
                  }
                />
                <StatTile label="Open tasks" figure={String(openTasks.length)} />
                <StatTile
                  label="Active campaigns"
                  figure={String(activeCampaigns.length)}
                />
              </div>

              {/* ---- MAIN + ASIDE ---- */}
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
                <section className="space-y-6">
                  <ContactOverviewFields data={contact.data} onPatch={onPatch} />

                  {/* Timeline */}
                  <div className="panel">
                    <h2 className="font-heading font-semibold text-base text-ink mb-4">
                      Interaction timeline
                    </h2>
                    <ContactTimeline
                      interactions={interactions}
                      hasMore={hasMoreInteractions}
                      onLoadMore={() =>
                        setInteractionLimit((n) => n + INTERACTION_PAGE)
                      }
                    />
                  </div>

                  <PipelineMemberships
                    contactId={contact.id}
                    contactName={name}
                    memberships={pipelineMemberships}
                    campaignMemberships={memberships}
                  />

                  <CampaignMemberships
                    memberships={memberships}
                    contactName={name}
                    unenrolling={unenrollMutation.isPending}
                    onUnenroll={(slug) => unenrollMutation.mutate(slug)}
                  />
                </section>

                <aside className="space-y-6">
                  <OpenTasksPanel tasks={openTasks} />

                  <div className="panel">
                    <span className="label mb-1">Notes</span>
                    <EditableField
                      label="Notes"
                      value={metadata.notes}
                      variant="textarea"
                      placeholder="Anything worth remembering about this contact…"
                      onSave={(v) => onPatch(["metadata", "notes"], v)}
                    />
                  </div>

                  <JarvisComposer refObj={{ type: "contact", id: contact.id }} />

                  <div className="panel ai-origin">
                    <div className="label mb-1">Jarvis's note</div>
                    <p className="text-sm text-ink-medium leading-relaxed">
                      Campaign memberships, step progress, and tasks live in
                      the CRM. Changes made here or by Jarvis update this page
                      live.
                    </p>
                  </div>
                </aside>
              </div>
            </div>
          );
        }}
      </QueryBoundary>
    </div>
  );
}
